import { useEffect, useState } from 'preact/hooks';
import type { Client } from '@/client';
import { GameState } from '@/game-state';
import {
  AlertContainer,
  ChangePassword,
  CharacterSelect,
  CreateAccount,
  CreateCharacter,
  InGame,
  Login,
  MainMenu,
} from '@/ui/containers';
import { ClientProvider, LocaleProvider, useGameState, useClient } from '@/ui/context';

export function WorldLoading() {
  const client = useClient();
  const [, update] = useState(0);
  useEffect(() => client.subscribeLoginProgress(() => update(n => n + 1)), [client]);
  if (!client.worldLoading) return null;
  // Normal in-world warps keep the last map visible. Never hide an actual
  // failure, and preserve the initial-login preload/recovery flow.
  if (client.state === GameState.InGame && !client.worldLoadError) return null;
  return (
    <div role='status' aria-live='polite' class='fixed inset-0 z-[10000] flex items-center justify-center bg-[#0b101d] p-6' onPointerDown={e => e.stopPropagation()}>
      <div class='card w-full max-w-sm bg-base-100 p-6 text-center shadow-xl'>
        <h2 class='mb-4 text-lg font-semibold'>Preparing your adventure</h2>
        {!client.worldLoadError && <span class='loading loading-spinner mx-auto mb-4' />}
        <p>{client.worldLoadError || client.loginStatus || 'Loading the world…'}</p>
        {client.worldLoadError && <button type='button' class='btn btn-primary mt-4' onClick={() => void client.sessionController.retryWorldLoading()}>Retry loading</button>}
        <button type='button' class='btn btn-ghost mt-3' onClick={() => client.disconnect()}>Back to login</button>
      </div>
    </div>
  );
}

function UiContent() {
  const gameState = useGameState();

  return (
    <>
      {(gameState === GameState.Initial ||
        gameState === GameState.Connected) && <MainMenu />}
      {gameState === GameState.Login && <Login />}
      {gameState === GameState.CreateAccount && <CreateAccount />}
      {gameState === GameState.CharacterSelect && <CharacterSelect />}
      {gameState === GameState.ChangePassword && <ChangePassword />}
      {gameState === GameState.CreateCharacter && <CreateCharacter />}
      {gameState === GameState.InGame && <InGame />}
      <WorldLoading />
    </>
  );
}

export function Ui({ client }: { client: Client }) {
  return (
    <ClientProvider client={client}>
      <LocaleProvider>
        <AlertContainer>
          <UiContent />
        </AlertContainer>
      </LocaleProvider>
    </ClientProvider>
  );
}
